'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CreatableCombobox } from '@/components/shared/CreatableCombobox';

const POSITION_SEP = '-';

function makePositionKey(row: string, cell: string): string {
  return `${row}${POSITION_SEP}${cell}`;
}

function parsePositionKey(key: string): [string, string] {
  const idx = key.indexOf(POSITION_SEP);
  if (idx <= 0) return [key, ''];
  return [key.slice(0, idx), key.slice(idx + 1)];
}

function deriveRowsAndCells(positionCells: string[]): {
  rows: string[];
  cells: string[];
} {
  const rowSet = new Set<string>();
  const cellSet = new Set<string>();
  for (const key of positionCells) {
    const [row, cell] = parsePositionKey(key);
    if (row) rowSet.add(row);
    if (cell) cellSet.add(cell);
  }
  return {
    rows: [...rowSet].sort((a, b) => a.localeCompare(b)),
    cells: [...cellSet].sort((a, b) => a.localeCompare(b)),
  };
}

interface EditableLabelProps {
  value: string;
  onRename: (newValue: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
}

function EditableLabel({
  value,
  onRename,
  disabled,
  ariaLabel,
  className,
}: EditableLabelProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setInputValue(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, value]);

  const handleSave = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed && trimmed !== value) {
      onRename(trimmed);
    }
    setEditing(false);
  }, [inputValue, value, onRename]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        setInputValue(value);
        setEditing(false);
        inputRef.current?.blur();
      }
    },
    [handleSave, value],
  );

  const handleBlur = useCallback(() => {
    setInputValue(value);
    setEditing(false);
  }, [value]);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="h-8 w-24 text-sm"
        disabled={disabled}
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className={className}
        style={{ minWidth: '1.5rem' }}
      >
        {value}
      </span>
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => setEditing(true)}
          aria-label={ariaLabel}
        >
          <Pencil className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export interface ShelfPositionGridProps {
  positionCells: string[];
  onChange: (positionCells: string[]) => void;
  disabled?: boolean;
  rowOptions?: string[];
  cellOptions?: string[];
  addRowLabel: string;
  addCellLabel: string;
  removeRowLabel: string;
  removeCellLabel: string;
  emptyText: string;
  title?: string;
  emptyOptionsText?: string;
  createHint?: string;
  /** Placeholder for the row input in the add-row popover (e.g. "e.g. 1") */
  rowPlaceholder?: string;
  /** Placeholder for the cell input in the add-cell popover (e.g. "e.g. A") */
  cellPlaceholder?: string;
  /** Short label for the confirm button inside add row/cell popovers (e.g. "Add") */
  confirmButtonLabel?: string;
  /** Aria-label for the edit row button (e.g. "Edit row") */
  editRowLabel?: string;
  /** Aria-label for the edit cell button (e.g. "Edit cell") */
  editCellLabel?: string;
}

export function ShelfPositionGrid({
  positionCells,
  onChange,
  disabled,
  rowOptions = [],
  cellOptions = [],
  addRowLabel,
  addCellLabel,
  removeRowLabel,
  removeCellLabel,
  emptyText,
  title,
  emptyOptionsText = 'No options',
  createHint = 'Press Enter to add',
  rowPlaceholder,
  cellPlaceholder,
  confirmButtonLabel = 'Add',
  editRowLabel,
  editCellLabel,
}: ShelfPositionGridProps): JSX.Element {
  const [preservedRows, setPreservedRows] = useState<Set<string>>(() => new Set());
  const [preservedCells, setPreservedCells] = useState<Set<string>>(() => new Set());

  const { rows, cells } = useMemo(() => {
    const derived = deriveRowsAndCells(positionCells);
    const allRows = new Set([...derived.rows, ...preservedRows]);
    const allCells = new Set([...derived.cells, ...preservedCells]);
    return {
      rows: [...allRows].sort((a, b) => a.localeCompare(b)),
      cells: [...allCells].sort((a, b) => a.localeCompare(b)),
    };
  }, [positionCells, preservedRows, preservedCells]);

  const [addRowOpen, setAddRowOpen] = useState(false);
  const [addCellOpen, setAddCellOpen] = useState(false);
  const [newRowValue, setNewRowValue] = useState('');
  const [newCellValue, setNewCellValue] = useState('');

  const positionSet = useMemo(
    () => new Set(positionCells),
    [positionCells],
  );

  const toggle = (row: string, cell: string): void => {
    const key = makePositionKey(row, cell);
    if (positionSet.has(key)) {
      const afterRemove = positionCells.filter((p) => p !== key);
      const remainingInRow = afterRemove.filter(
        (p) => parsePositionKey(p)[0] === row,
      ).length;
      const remainingInCell = afterRemove.filter(
        (p) => parsePositionKey(p)[1] === cell,
      ).length;
      if (remainingInRow === 0) {
        setPreservedRows((prev) => new Set([...prev, row]));
      }
      if (remainingInCell === 0) {
        setPreservedCells((prev) => new Set([...prev, cell]));
      }
      onChange(afterRemove);
    } else {
      setPreservedRows((prev) => {
        const next = new Set(prev);
        next.delete(row);
        return next;
      });
      setPreservedCells((prev) => {
        const next = new Set(prev);
        next.delete(cell);
        return next;
      });
      onChange([...positionCells, key]);
    }
  };

  const removeRow = (row: string): void => {
    setPreservedRows((prev) => {
      const next = new Set(prev);
      next.delete(row);
      return next;
    });
    onChange(
      positionCells.filter((p) => parsePositionKey(p)[0] !== row),
    );
  };

  const removeCell = (cell: string): void => {
    setPreservedCells((prev) => {
      const next = new Set(prev);
      next.delete(cell);
      return next;
    });
    onChange(
      positionCells.filter((p) => parsePositionKey(p)[1] !== cell),
    );
  };

  const handleAddRow = (): void => {
    const row = newRowValue.trim().toUpperCase() || newRowValue.trim();
    if (!row) return;
    const firstCell = cells[0] ?? 'A';
    const key = makePositionKey(row, firstCell);
    if (!positionSet.has(key)) {
      onChange([...positionCells, key]);
    }
    setNewRowValue('');
    setAddRowOpen(false);
  };

  const handleAddCell = (): void => {
    const cell = newCellValue.trim().toUpperCase() || newCellValue.trim();
    if (!cell) return;
    const firstRow = rows[0] ?? '1';
    const key = makePositionKey(firstRow, cell);
    if (!positionSet.has(key)) {
      onChange([...positionCells, key]);
    }
    setNewCellValue('');
    setAddCellOpen(false);
  };

  const normalizeName = (raw: string): string =>
    raw.trim().toUpperCase() || raw.trim();

  const renameRow = useCallback(
    (oldRow: string, newRowRaw: string): void => {
      const newRow = normalizeName(newRowRaw);
      if (!newRow || newRow === oldRow) return;
      if (rows.includes(newRow)) return;
      const next = positionCells.map((key) => {
        const [r, c] = parsePositionKey(key);
        return r === oldRow ? makePositionKey(newRow, c) : key;
      });
      onChange(next);
      setPreservedRows((prev) => {
        if (!prev.has(oldRow)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(oldRow);
        nextSet.add(newRow);
        return nextSet;
      });
    },
    [positionCells, rows, onChange],
  );

  const renameCell = useCallback(
    (oldCell: string, newCellRaw: string): void => {
      const newCell = normalizeName(newCellRaw);
      if (!newCell || newCell === oldCell) return;
      if (cells.includes(newCell)) return;
      const next = positionCells.map((key) => {
        const [r, c] = parsePositionKey(key);
        return c === oldCell ? makePositionKey(r, newCell) : key;
      });
      onChange(next);
      setPreservedCells((prev) => {
        if (!prev.has(oldCell)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(oldCell);
        nextSet.add(newCell);
        return nextSet;
      });
    },
    [positionCells, cells, onChange],
  );

  const isEmpty = rows.length === 0 && cells.length === 0;

  return (
    <div className="space-y-4 rounded-md border p-4">
      {title && (
        <div className="text-sm font-medium">{title}</div>
      )}
      {isEmpty ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border border-border px-3 py-2 text-left font-normal text-muted-foreground">
                  {' '}
                </th>
                {cells.map((cell) => (
                  <th
                    key={cell}
                    className="border border-border px-3 py-2 text-center"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <div className="rounded-md bg-muted/30 px-2.5 py-1">
                        <EditableLabel
                          value={cell}
                          onRename={(v) => renameCell(cell, v)}
                          disabled={disabled}
                          ariaLabel={editCellLabel ?? removeCellLabel}
                        />
                      </div>
                      <span className="w-px self-stretch min-h-5 bg-border" aria-hidden />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        disabled={disabled}
                        onClick={() => removeCell(cell)}
                        aria-label={removeCellLabel}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </th>
                ))}
                <th className="border border-border px-3 py-2 w-10">
                  <Popover open={addCellOpen} onOpenChange={setAddCellOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={disabled}
                        aria-label={addCellLabel}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-56"
                      side="bottom"
                      align="end"
                      sideOffset={4}
                    >
                      <div className="space-y-2">
                        <CreatableCombobox
                          options={[...new Set([...cellOptions, ...cells])].sort((a, b) => a.localeCompare(b))}
                          value={newCellValue}
                          onChange={setNewCellValue}
                          placeholder={cellPlaceholder ?? addCellLabel}
                          emptyText={emptyOptionsText}
                          createHint={createHint}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={handleAddCell}
                          disabled={!newCellValue.trim()}
                          className="w-full"
                        >
                          {confirmButtonLabel}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row}>
                  <td className="border border-border px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="rounded-md bg-muted/30 px-2.5 py-1">
                        <EditableLabel
                          value={row}
                          onRename={(v) => renameRow(row, v)}
                          disabled={disabled}
                          ariaLabel={editRowLabel ?? removeRowLabel}
                          className="font-medium"
                        />
                      </div>
                      <span className="w-px self-stretch min-h-5 bg-border" aria-hidden />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        disabled={disabled}
                        onClick={() => removeRow(row)}
                        aria-label={removeRowLabel}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                  {cells.map((cell) => {
                    const key = makePositionKey(row, cell);
                    const checked = positionSet.has(key);
                    return (
                      <td
                        key={key}
                        className="border border-border px-3 py-2 text-center"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => toggle(row, cell)}
                          aria-label={`${row} ${cell}`}
                        />
                      </td>
                    );
                  })}
                  <td className="border border-border px-3 py-2" />
                </tr>
              ))}
              <tr>
                <td className="border border-border px-3 pt-3 pb-2" colSpan={cells.length + 2}>
                  <Popover open={addRowOpen} onOpenChange={setAddRowOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={disabled}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {addRowLabel}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-56"
                      side="bottom"
                      align="start"
                      sideOffset={4}
                    >
                      <div className="space-y-2">
                        <CreatableCombobox
                          options={[...new Set([...rowOptions, ...rows])].sort((a, b) => a.localeCompare(b))}
                          value={newRowValue}
                          onChange={setNewRowValue}
                          placeholder={rowPlaceholder ?? addRowLabel}
                          emptyText={emptyOptionsText}
                          createHint={createHint}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={handleAddRow}
                          disabled={!newRowValue.trim()}
                          className="w-full"
                        >
                          {confirmButtonLabel}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {isEmpty && (
        <div className="flex flex-wrap gap-2">
          <Popover open={addRowOpen} onOpenChange={setAddRowOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
              >
                <Plus className="mr-1 h-4 w-4" />
                {addRowLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-56"
              side="bottom"
              align="start"
              sideOffset={4}
            >
              <div className="space-y-2">
                <CreatableCombobox
                  options={[...new Set([...rowOptions, ...rows])].sort((a, b) => a.localeCompare(b))}
                  value={newRowValue}
                  onChange={setNewRowValue}
                  placeholder={rowPlaceholder ?? addRowLabel}
                  emptyText={emptyOptionsText}
                  createHint={createHint}
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleAddRow}
                  disabled={!newRowValue.trim()}
                  className="w-full"
                >
                  {confirmButtonLabel}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={addCellOpen} onOpenChange={setAddCellOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
              >
                <Plus className="mr-1 h-4 w-4" />
                {addCellLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-56"
              side="bottom"
              align="start"
              sideOffset={4}
            >
              <div className="space-y-2">
                <CreatableCombobox
                  options={[...new Set([...cellOptions, ...cells])].sort((a, b) => a.localeCompare(b))}
                  value={newCellValue}
                  onChange={setNewCellValue}
                  placeholder={cellPlaceholder ?? addCellLabel}
                  emptyText={emptyOptionsText}
                  createHint={createHint}
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleAddCell}
                  disabled={!newCellValue.trim()}
                  className="w-full"
                >
                  {confirmButtonLabel}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
